/**
 * 🚀 SPACE CABIN PROFILE - Cabine du Vaisseau Spatial
 * Design "Celestial Cabin VIP" - Expérience futuriste pour les passagers VIP
 * 
 * "Bienvenue à bord, Commandant."
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  User, Edit, Shield, CreditCard, Check, HelpCircle,
  Phone, Mail, MapPin, Calendar, Star, Users, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import ThemeSelector, { ThemeIndicator } from '../ThemeToggle';
import { BirthdayCountdown } from '../BirthdayGift';

// 🎨 Configuration des niveaux avec style spatial
const LOYALTY_STYLES = {
  Bronze: { 
    icon: '🛸', 
    gradient: 'from-amber-700 to-amber-900',
    glow: 'rgba(217, 119, 6, 0.3)',
    ring: '#d97706'
  },
  Argent: { 
    icon: '🌙', 
    gradient: 'from-slate-400 to-slate-600',
    glow: 'rgba(148, 163, 184, 0.3)',
    ring: '#94a3b8'
  },
  Or: { 
    icon: '⭐', 
    gradient: 'from-yellow-400 to-amber-500',
    glow: 'rgba(251, 191, 36, 0.4)',
    ring: '#fbbf24'
  },
  Diamant: { 
    icon: '💎', 
    gradient: 'from-cyan-400 to-blue-500',
    glow: 'rgba(6, 182, 212, 0.4)',
    ring: '#06b6d4'
  }
};

const SpaceCabinProfile = ({
  customer,
  debtInfo,
  totpStatus,
  largeTextMode,
  setLargeTextMode,
  setShowProfileEditor,
  setShow2FASetup,
  setShow2FAGuide,
  setShowHeartbeats,
  heartbeatsTripleTap,
  setHeartbeatsTripleTap,
  isDarkMode,
  badgeIcons = {},
  badgeNames = {}
}) => {
  const levelName = customer?.loyaltyLevel?.label?.split(' ')[1] || 'Bronze';
  const levelStyle = LOYALTY_STYLES[levelName] || LOYALTY_STYLES.Bronze;

  // Animation stagger pour les éléments
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="max-w-4xl mx-auto mb-6"
    >
      {/* 🌌 CONTENEUR PRINCIPAL - CABINE SPATIALE */}
      <div 
        className="relative overflow-hidden rounded-3xl"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
          boxShadow: `0 0 60px ${levelStyle.glow}, 0 25px 50px rgba(0,0,0,0.5)`,
          border: '1px solid rgba(148, 163, 184, 0.1)'
        }}
      >
        {/* 🌠 Background stars animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-white"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.5 + 0.2
              }}
              animate={{
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        {/* 🔷 HUD Border Effect */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent" />
        
        {/* Corner HUD elements */}
        <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-cyan-500/50" />
        <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-cyan-500/50" />
        <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-purple-500/50" />
        <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-purple-500/50" />

        {/* 🎛️ TOP BAR - Contrôles du vaisseau */}
        <div className="relative p-4 border-b border-white/5">
          <div className="flex items-center justify-between">
            {/* Station ID */}
            <motion.div 
              variants={itemVariants}
              className="flex items-center gap-2"
            >
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] font-mono tracking-[0.3em] text-cyan-500/80 uppercase">
                Station LPE • En ligne
              </span>
            </motion.div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <ThemeSelector className="scale-90" />
              <div className="flex gap-1 bg-black/40 rounded-lg p-1 border border-white/10">
                <motion.button
                  onClick={() => setShowProfileEditor(true)}
                  className="p-2 rounded-md bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 transition-all"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  title="Modifier mon profil"
                >
                  <Edit className="w-4 h-4" />
                </motion.button>
                <motion.button
                  onClick={() => {
                    const newValue = !largeTextMode;
                    setLargeTextMode(newValue);
                    localStorage.setItem('largeTextMode', newValue.toString());
                  }}
                  className={`p-2 rounded-md transition-all ${
                    largeTextMode 
                      ? 'bg-yellow-500/20 text-yellow-400' 
                      : 'bg-white/5 hover:bg-white/10 text-gray-400'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="text-sm">👁️</span>
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* 👨‍🚀 HERO SECTION - Avatar du Commandant */}
        <div className="relative px-6 py-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            
            {/* 🖼️ PHOTO MASSIVE - Avatar du Capitaine */}
            <motion.div 
              variants={itemVariants}
              className="relative flex-shrink-0"
              onClick={() => {
                const isConfidential = localStorage.getItem(`heartbeats_confidential_${customer?.id}`) === 'true';
                if (isConfidential) {
                  setHeartbeatsTripleTap(prev => {
                    const newCount = prev + 1;
                    if (newCount >= 3) {
                      const savedPin = localStorage.getItem(`heartbeats_pin_${customer?.id}`);
                      if (savedPin) {
                        const enteredPin = prompt('🔐 Code secret Heartbeats :');
                        if (enteredPin === savedPin) {
                          setShowHeartbeats(true);
                        } else if (enteredPin !== null) {
                          alert('❌ Code incorrect');
                        }
                      } else {
                        setShowHeartbeats(true);
                      }
                      return 0;
                    }
                    setTimeout(() => setHeartbeatsTripleTap(0), 1000);
                    return newCount;
                  });
                }
              }}
            >
              {/* Anneau de force - effet spatial */}
              <motion.div
                className="absolute -inset-3 rounded-full border-2 border-dashed opacity-50"
                style={{ borderColor: levelStyle.ring }}
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              />
              <motion.div
                className="absolute -inset-6 rounded-full border border-dashed opacity-25"
                style={{ borderColor: levelStyle.ring }}
                animate={{ rotate: -360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              />
              
              {/* Photo container */}
              <div 
                className="relative w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden cursor-pointer"
                style={{
                  boxShadow: `0 0 40px ${levelStyle.glow}, 0 0 80px ${levelStyle.glow}`,
                  border: `4px solid ${levelStyle.ring}`
                }}
              >
                {customer.profilePhoto ? (
                  <img 
                    src={customer.profilePhoto} 
                    alt={customer.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                    <User className="w-20 h-20 text-cyan-500/30" />
                  </div>
                )}
                
                {/* Scan line effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/10 to-transparent"
                  animate={{ y: ['-100%', '100%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                />
              </div>

              {/* Badge niveau */}
              <motion.div
                className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold"
                style={{
                  background: `linear-gradient(135deg, ${levelStyle.ring}40, ${levelStyle.ring}20)`,
                  border: `1px solid ${levelStyle.ring}`,
                  boxShadow: `0 0 20px ${levelStyle.glow}`
                }}
                whileHover={{ scale: 1.05 }}
              >
                <span className="mr-1">{levelStyle.icon}</span>
                <span className="text-white">{levelName}</span>
              </motion.div>

              {/* Mineur badge */}
              {customer.isMinor && (
                <div className="absolute -top-2 -right-2 bg-amber-500 text-black text-[10px] font-bold px-2 py-1 rounded-lg shadow-lg">
                  Mineur
                </div>
              )}
            </motion.div>

            {/* 📋 INFO COMMANDANT */}
            <motion.div variants={itemVariants} className="flex-1 text-center md:text-left">
              <motion.h2 
                className="text-3xl md:text-4xl font-bold text-white mb-2"
                style={{ textShadow: '0 0 30px rgba(6, 182, 212, 0.3)' }}
              >
                {customer.name}
              </motion.h2>
              
              <p className="text-cyan-500/70 font-mono text-sm tracking-wider mb-4">
                PASSAGER VIP • DEPUIS {new Date(customer.createdAt || Date.now()).getFullYear()}
              </p>

              {/* Badges status */}
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <BirthdayCountdown birthDate={customer.birthDate} />
                
                {debtInfo?.hasDebt ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold bg-red-500/20 text-red-400 border border-red-500/30 animate-pulse">
                    <CreditCard className="w-4 h-4" />
                    {debtInfo.balance.toFixed(2).replace('.', ',')}€ dû
                  </span>
                ) : debtInfo && (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    <Check className="w-4 h-4" />
                    À jour
                  </span>
                )}
              </div>

              <ThemeIndicator className="mt-3 justify-center md:justify-start" />
            </motion.div>
          </div>
        </div>

        {/* 📊 STATS DECK - Tableau de bord */}
        <motion.div 
          variants={itemVariants}
          className="px-6 pb-6"
        >
          <div className="grid grid-cols-3 gap-3">
            {/* Niveau Fidélité */}
            <div 
              className="relative p-4 rounded-2xl text-center overflow-hidden"
              style={{
                background: 'rgba(6, 182, 212, 0.05)',
                border: '1px solid rgba(6, 182, 212, 0.2)'
              }}
            >
              <div className="absolute top-2 left-2 text-[8px] font-mono text-cyan-500/50 tracking-widest">RANG</div>
              <div className="text-4xl mb-1">{levelStyle.icon}</div>
              <div className="text-xl font-bold text-white">{levelName}</div>
              {customer.loyaltyLevel?.nextLevel && (
                <div className="text-[10px] text-gray-500 mt-1">
                  {customer.loyaltyLevel.cardsToNext} → {customer.loyaltyLevel.nextLevel}
                </div>
              )}
            </div>

            {/* Cartes complétées */}
            <div 
              className="relative p-4 rounded-2xl text-center overflow-hidden"
              style={{
                background: 'rgba(139, 92, 246, 0.05)',
                border: '1px solid rgba(139, 92, 246, 0.2)'
              }}
            >
              <div className="absolute top-2 left-2 text-[8px] font-mono text-purple-500/50 tracking-widest">CARTES</div>
              <motion.div 
                className="text-4xl font-bold text-purple-400"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.3 }}
              >
                {customer.completedCardsCount || 0}
              </motion.div>
              <div className="text-xs text-gray-500">complétées</div>
            </div>

            {/* Filleuls */}
            <div 
              className="relative p-4 rounded-2xl text-center overflow-hidden"
              style={{
                background: 'rgba(236, 72, 153, 0.05)',
                border: '1px solid rgba(236, 72, 153, 0.2)'
              }}
            >
              <div className="absolute top-2 left-2 text-[8px] font-mono text-pink-500/50 tracking-widest">RECRUES</div>
              <motion.div 
                className="text-4xl font-bold text-pink-400"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.4 }}
              >
                {customer.referralCount || 0}
              </motion.div>
              <div className="text-xs text-gray-500">filleuls</div>
            </div>
          </div>
        </motion.div>

        {/* 🗃️ DATA BAY - Informations personnelles */}
        <motion.div 
          variants={itemVariants}
          className="px-6 pb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Colonne Gauche - Coordonnées */}
            <div className="space-y-3">
              <div className="text-[10px] font-mono text-cyan-500/60 tracking-[0.3em] uppercase mb-3 flex items-center gap-2">
                <div className="w-8 h-px bg-gradient-to-r from-cyan-500/50 to-transparent" />
                Coordonnées
              </div>
              
              {/* Téléphone */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-cyan-500/20 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 font-mono">TÉLÉPHONE</div>
                  <div className="text-white font-medium">{customer.phone}</div>
                </div>
              </div>

              {/* Email */}
              {customer.email ? (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-pink-500/20 transition-all group">
                  <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-pink-400" />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <div className="text-[10px] text-gray-500 font-mono flex items-center gap-2">
                      EMAIL
                      {customer.emailVerified ? (
                        <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-[8px] font-bold">✓ VÉRIFIÉ</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-[8px] font-bold animate-pulse">NON VÉRIFIÉ</span>
                      )}
                    </div>
                    <div className="text-white font-medium truncate">{customer.email}</div>
                  </div>
                </div>
              ) : (
                /* Pas d'email - Encourager à l'ajouter */
                <motion.div 
                  className="p-3 rounded-xl border border-dashed border-pink-500/30 bg-pink-500/5 cursor-pointer hover:bg-pink-500/10 transition-all"
                  onClick={() => setShowProfileEditor(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-pink-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-pink-400 font-medium text-sm">Ajouter mon email</div>
                      <div className="text-[10px] text-gray-500">Pour me connecter + récupérer mon compte</div>
                    </div>
                    <Sparkles className="w-4 h-4 text-pink-400 animate-pulse" />
                  </div>
                </motion.div>
              )}

              {/* 🔐 Email Login Feature - Nouveau ! */}
              {customer.email && !customer.emailVerified && (
                <motion.div 
                  className="p-4 rounded-xl border border-cyan-500/30 overflow-hidden relative"
                  style={{
                    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Effet de scan */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/5 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  />
                  
                  <div className="relative z-10">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">🔑</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-cyan-400 font-bold text-sm mb-1">
                          Débloque la connexion par email !
                        </h4>
                        <p className="text-gray-400 text-xs leading-relaxed mb-3">
                          Valide ton email pour pouvoir te connecter avec, 
                          et ne jamais perdre l'accès à ton compte VIP !
                        </p>
                        <motion.button
                          onClick={async () => {
                            try {
                              const API = process.env.REACT_APP_BACKEND_URL;
                              const res = await fetch(`${API}/api/customers/${customer.id}/send-email-verification`, {
                                method: 'POST'
                              });
                              const data = await res.json();
                              if (data.success) {
                                alert('📧 Email de vérification envoyé ! Check ta boîte mail (et les spams)');
                              } else {
                                alert(data.detail || 'Erreur lors de l\'envoi');
                              }
                            } catch (err) {
                              alert('Erreur de connexion');
                            }
                          }}
                          className="w-full py-2.5 rounded-lg font-bold text-sm text-white flex items-center justify-center gap-2"
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)',
                            boxShadow: '0 0 20px rgba(6, 182, 212, 0.3)'
                          }}
                          whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(6, 182, 212, 0.5)' }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Mail className="w-4 h-4" />
                          Envoyer le lien de validation
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Email vérifié - Confirmation visuelle */}
              {customer.email && customer.emailVerified && (
                <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-green-400 text-xs font-medium">Email vérifié</div>
                      <div className="text-[10px] text-gray-500">Tu peux te connecter avec ton email + code</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Adresse */}
              {(customer.address || customer.city) && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-green-500/20 transition-all group">
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 font-mono">LOCALISATION</div>
                    <div className="text-white font-medium text-sm">
                      {customer.address && `${customer.address}, `}{customer.postalCode} {customer.city}
                    </div>
                  </div>
                </div>
              )}

              {/* Date naissance */}
              {customer.birthDate && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-yellow-500/20 transition-all group">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-500 font-mono">NAISSANCE</div>
                    <div className="text-white font-medium">{new Date(customer.birthDate).toLocaleDateString('fr-FR')}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Colonne Droite - Sécurité & Badges */}
            <div className="space-y-3">
              <div className="text-[10px] font-mono text-purple-500/60 tracking-[0.3em] uppercase mb-3 flex items-center gap-2">
                <div className="w-8 h-px bg-gradient-to-r from-purple-500/50 to-transparent" />
                Sécurité & Badges
              </div>

              {/* 2FA Panel */}
              <div 
                className="p-4 rounded-xl transition-all"
                style={{
                  background: totpStatus?.enabled 
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)'
                    : 'rgba(255, 255, 255, 0.03)',
                  border: totpStatus?.enabled 
                    ? '1px solid rgba(34, 197, 94, 0.3)' 
                    : '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      totpStatus?.enabled ? 'bg-green-500/20' : 'bg-white/5'
                    }`}>
                      <Shield className={`w-5 h-5 ${totpStatus?.enabled ? 'text-green-400' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <div className="text-white font-medium">Double Authentification</div>
                      <div className="text-xs text-gray-400">
                        {totpStatus?.enabled ? '🔒 Compte protégé' : '⚠️ Non activé'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShow2FAGuide(true)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-500/10 hover:bg-blue-500/20 transition-colors"
                    >
                      <HelpCircle className="w-4 h-4 text-blue-400" />
                    </button>
                    {totpStatus?.enabled ? (
                      <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                    ) : (
                      <Button
                        onClick={() => setShow2FASetup(true)}
                        size="sm"
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs h-8"
                      >
                        Activer
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Badges acquis */}
              {customer.badges?.length > 0 && (
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <div className="text-[10px] text-gray-500 font-mono mb-3">BADGES ACQUIS</div>
                  <div className="flex flex-wrap gap-2">
                    {customer.badges.map(badge => (
                      <span
                        key={badge}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 text-cyan-300"
                      >
                        {badgeIcons[badge]} {badgeNames[badge]}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Réseaux sociaux - Version compacte */}
              {(customer.facebook || customer.instagram || customer.snapchat || customer.tiktok) && (
                <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                  <div className="text-[10px] text-gray-500 font-mono mb-3">RÉSEAUX CONNECTÉS</div>
                  <div className="flex flex-wrap gap-2">
                    {customer.facebook && <span className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-lg">📘</span>}
                    {customer.instagram && <span className="w-8 h-8 rounded-lg bg-pink-500/20 flex items-center justify-center text-lg">📸</span>}
                    {customer.snapchat && <span className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center text-lg">👻</span>}
                    {customer.tiktok && <span className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center text-lg">🎵</span>}
                    {customer.whatsapp && <span className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-lg">💬</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* 🌠 Footer HUD */}
        <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[10px] font-mono text-gray-600">
            <Sparkles className="w-3 h-3 text-cyan-500/50" />
            <span>LA PETITE ÉPICERIE • SYSTÈME FIDÉLITÉ v2.0</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1 h-1 rounded-full bg-green-500" />
            <div className="w-1 h-1 rounded-full bg-cyan-500" />
            <div className="w-1 h-1 rounded-full bg-purple-500" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SpaceCabinProfile;
